import { useMemo } from 'react';
import { CreditCard, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMySubscription, useReportPayment } from '@/hooks/useSubscription';
import { useAuthStore } from '@/context/authStore';
import { formatPrice } from '@/utils/format';
import toast from 'react-hot-toast';

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/**
 * Aviso de la suscripción en el Dashboard.
 *
 * Aparece SOLO cuando hay algo que saber: la prueba gratuita está corriendo, estamos en
 * el período de pago del mes, el pago está vencido, avisaron un pago que falta confirmar,
 * o la cuenta está suspendida. Si está todo al día no se muestra nada: el Dashboard es el
 * lugar de trabajo del cliente, no el de nuestra cobranza.
 *
 * Durante la prueba no se habla de pagos ni de deuda: se muestra cuándo termina.
 *
 * Solo lo ve quien administra la veterinaria (permiso de Usuarios), no el cajero ni el
 * veterinario.
 */
export default function SubscriptionNotice() {
  const user = useAuthStore((s) => s.user);
  const puedeVer = user?.effective_permissions?.includes('users') ?? false;

  const { data: sub } = useMySubscription();
  const reportPayment = useReportPayment();

  const aviso = useMemo(() => {
    if (!sub) return null;

    const hoy = new Date();
    const dia = hoy.getDate();
    const mesActual = MESES[hoy.getMonth()];

    // Vencimiento: el que tenga cargado la suscripción; si no, el 15
    const vence = sub.next_payment_date ? new Date(sub.next_payment_date) : null;
    const diaVencimiento = vence ? vence.getDate() : 15;

    // ¿Está pendiente el mes en curso?
    //
    // No se mira payment_status, porque queda clavado en "paid" apenas se confirma el
    // primer pago: nada lo devuelve a pendiente al empezar el mes siguiente, asi que el
    // cliente no volvia a ver el aviso nunca mas. Se deduce del proximo vencimiento, que
    // es el campo hecho para esto: si cae en este mes o antes, el periodo esta por pagar.
    //
    // Pagar adelantado queda contemplado: al confirmar, el vencimiento se corre al mes
    // siguiente y el aviso no aparece.
    const cicloPorPagar = vence
      ? vence.getFullYear() < hoy.getFullYear() ||
        (vence.getFullYear() === hoy.getFullYear() && vence.getMonth() <= hoy.getMonth())
      : sub.payment_status !== 'paid';

    // Prueba gratuita: no hay nada que abonar todavía. Se avisa cuándo termina, no
    // cuánto "debe", que es justo lo que no corresponde en una prueba.
    if (sub.status === 'trial') {
      const fin = sub.end_date ? new Date(sub.end_date) : null;
      if (!fin) {
        return {
          tono: 'gris' as const,
          titulo: 'Estás usando la prueba gratuita',
          detalle: 'Cuando termine te avisamos para activar la suscripción.',
          mostrarBoton: false,
        };
      }

      const finDia = new Date(fin.getFullYear(), fin.getMonth(), fin.getDate());
      const hoyDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
      const dias = Math.round((finDia.getTime() - hoyDia.getTime()) / 86400000);
      const fechaFin = `${fin.getDate()} de ${MESES[fin.getMonth()]}`;

      if (dias < 0) {
        return {
          tono: 'rojo' as const,
          titulo: 'Tu prueba gratuita terminó',
          detalle: `Venció el ${fechaFin}. Escribinos para activar la suscripción.`,
          mostrarBoton: false,
        };
      }

      const cuanto =
        dias === 0 ? 'Termina hoy' : dias === 1 ? 'Queda 1 día' : `Quedan ${dias} días`;
      return {
        tono: dias <= 7 ? ('ambar' as const) : ('gris' as const),
        titulo: 'Estás usando la prueba gratuita',
        detalle: `${cuanto}: vence el ${fechaFin}.`,
        mostrarBoton: false,
      };
    }

    if (sub.status === 'suspended' || sub.status === 'cancelled') {
      return {
        tono: 'rojo' as const,
        titulo: sub.status === 'suspended' ? 'Tu suscripción está suspendida' : 'Tu suscripción está cancelada',
        detalle: 'Escribinos para reactivarla.',
        mostrarBoton: false,
      };
    }

    // Avisaron el pago: falta que lo confirmemos
    if (sub.payment_reported_at && sub.payment_status !== 'paid') {
      return {
        tono: 'gris' as const,
        titulo: 'Recibimos tu aviso de pago',
        detalle: 'Lo estamos confirmando. No hace falta que avises de nuevo.',
        mostrarBoton: false,
      };
    }

    // Un vencimiento de un mes anterior ya esta vencido, sin importar que dia sea hoy:
    // si no, el 9 de octubre una deuda de septiembre salia en ambar como recien empezada.
    const vencidoDeMesAnterior = !!vence && (
      vence.getFullYear() < hoy.getFullYear() ||
      (vence.getFullYear() === hoy.getFullYear() && vence.getMonth() < hoy.getMonth())
    );

    if (
      sub.payment_status === 'overdue' ||
      vencidoDeMesAnterior ||
      (cicloPorPagar && dia > diaVencimiento)
    ) {
      return {
        tono: 'rojo' as const,
        titulo: `Tenés la suscripción de ${mesActual} pendiente`,
        detalle: `Vencía el ${diaVencimiento}. Son ${formatPrice(sub.price_monthly)} por mes.`,
        mostrarBoton: true,
      };
    }

    // Período de pago del mes: del 1 al día de vencimiento
    if (cicloPorPagar && dia <= diaVencimiento) {
      return {
        tono: 'ambar' as const,
        titulo: `Recordá abonar la suscripción de ${mesActual}`,
        detalle: `${formatPrice(sub.price_monthly)} · podés pagar hasta el ${diaVencimiento}.`,
        // El precio se revisa cada 3 meses. Se avisa para que el ajuste no llegue de
        // sorpresa; el monto puede no estar actualizado todavia, por eso no se promete uno.
        aviso: sub.is_adjustment_month
          ? 'Este mes corresponde la revisión trimestral del precio. Te avisamos si cambia.'
          : undefined,
        mostrarBoton: true,
      };
    }

    return null;
  }, [sub]);

  if (!puedeVer || !aviso) return null;

  const estilos = {
    ambar: { caja: 'bg-amber-50 border-amber-300', texto: 'text-amber-900', sub: 'text-amber-800', icono: 'text-amber-600' },
    rojo: { caja: 'bg-red-50 border-red-300', texto: 'text-red-900', sub: 'text-red-800', icono: 'text-red-600' },
    gris: { caja: 'bg-gray-50 border-gray-300', texto: 'text-gray-800', sub: 'text-gray-600', icono: 'text-gray-500' },
  }[aviso.tono];

  const Icono = aviso.tono === 'rojo' ? AlertTriangle : aviso.tono === 'gris' ? CheckCircle2 : Clock;

  const handleReportar = async () => {
    try {
      await reportPayment.mutateAsync();
      toast.success('Listo, recibimos tu aviso. Lo confirmamos a la brevedad.');
    } catch {
      toast.error('No se pudo enviar el aviso. Probá de nuevo en un rato.');
    }
  };

  return (
    <div className={`rounded-xl border-2 p-4 flex flex-wrap items-center gap-4 ${estilos.caja}`}>
      <Icono className={`h-6 w-6 flex-shrink-0 ${estilos.icono}`} />
      <div className="flex-1 min-w-[200px]">
        <p className={`font-semibold ${estilos.texto}`}>{aviso.titulo}</p>
        <p className={`text-sm ${estilos.sub}`}>{aviso.detalle}</p>
        {'aviso' in aviso && aviso.aviso && (
          <p className={`text-sm mt-1 ${estilos.sub} opacity-90`}>{aviso.aviso}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {aviso.mostrarBoton && (
          <button
            onClick={handleReportar}
            disabled={reportPayment.isPending}
            className="px-3 py-2 rounded-lg bg-white border border-current text-sm font-medium hover:bg-gray-50 disabled:opacity-60"
          >
            {reportPayment.isPending ? 'Enviando...' : 'Ya pagué'}
          </button>
        )}
        <Link
          to="/dashboard/my-subscription"
          className="px-3 py-2 rounded-lg bg-white/70 text-sm font-medium hover:bg-white flex items-center gap-1.5"
        >
          <CreditCard className="h-4 w-4" />
          Ver datos de pago
        </Link>
      </div>
    </div>
  );
}
